/**
 * <product-recommendations-component> - lazy-loaded product recommendations.
 *
 * Shopify's recommendations object is only populated when the section is
 * fetched via /recommendations/products?section_id=…&product_id=…&intent=…
 *
 * Flow:
 *   1. Section renders empty on first paint (zero render cost on the page).
 *   2. An IntersectionObserver triggers a fetch as soon as the component
 *      gets close to the viewport.
 *   3. The response HTML is parsed; we swap the inner content in place,
 *      preserving the host element (so the section keeps its id and the
 *      Section Rendering API still owns it).
 *
 * Required data attributes on the host element:
 *   data-product-id  - the current product's id
 *   data-section-id  - the section id (for the Shopify endpoint)
 *   data-intent      - "related" | "complementary"
 *   data-limit       - number of products to fetch
 */

import { Component } from "@theme/component";

class ProductRecommendationsComponent extends Component {
  /** @type {boolean} */
  #loaded = false;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    // If Liquid already managed to populate the inner content (e.g. when this
    // section is itself fetched via the recommendations endpoint), skip.
    if (this.#hasContent()) {
      this.#loaded = true;
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#observer?.disconnect();
            this.#load();
            break;
          }
        }
      },
      { rootMargin: "400px 0px" },
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  #hasContent() {
    const inner = this.querySelector("[data-recommendations-inner]");
    return !!inner && inner.children.length > 0;
  }

  #buildURL() {
    const { productId, sectionId, intent, limit } = this.dataset;
    if (!productId || !sectionId) return null;

    // Préfixe la racine localisée (`/`, `/en/`, …) pour que Shopify rende la
    // section dans la bonne langue. Sans ça, le fetch part sur la locale par
    // défaut et la traduction (heading, prix…) ne s'applique pas.
    const root = window.Shopify?.routes?.root || "/";
    const url = new URL(`${root}recommendations/products`, window.location.origin);
    url.searchParams.set("section_id", sectionId);
    url.searchParams.set("product_id", productId);
    if (intent) url.searchParams.set("intent", intent);
    if (limit) url.searchParams.set("limit", limit);
    return url;
  }

  async #load() {
    if (this.#loaded) return;
    this.#loaded = true;

    const url = this.#buildURL();
    if (!url) return;

    this.dataset.loading = "true";

    try {
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();

      const fragment = new DOMParser().parseFromString(html, "text/html");
      const newInner = fragment.querySelector("[data-recommendations-inner]");
      const currentInner = this.querySelector("[data-recommendations-inner]");

      if (!newInner || !currentInner) return;

      // If the fetched response has no products (empty inner), hide the section.
      if (newInner.children.length === 0) {
        this.hidden = true;
        return;
      }

      currentInner.replaceWith(newInner);
    } catch (error) {
      console.error("[product-recommendations] load failed:", error);
      this.hidden = true;
    } finally {
      delete this.dataset.loading;
    }
  }
}

if (!customElements.get("product-recommendations-component")) {
  customElements.define("product-recommendations-component", ProductRecommendationsComponent);
}
